import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Dither from './Dither';

const labProjects = [
    {
        id: '00',
        icon: (<img src="/images/isho-logo.png" className="w-10 md:w-12 h-auto object-contain drop-shadow-[0_0_10px_rgba(244,114,182,0.8)]" alt="ISHO" />),
        title: 'ISHO',
        subtitle: 'INTELLIGENT COMPANION & SOCRATIC TUTOR.',
        status: 'ONLINE',
        statusColor: 'text-pink-400 border-pink-400/30',
        description: 'MEET ISHO. SHE IS DESIGNED NOT JUST TO GIVE YOU ANSWERS, BUT TO GUIDE YOU TO THEM. CAPABLE OF PROCESSING TEXT, ANALYZING IMAGES, AND READING DOCUMENTS WHILE SECURELY MAINTAINING YOUR PRIVATE CHAT ARCHIVES. SHE OVERSEES THE SERVERS, BUT SHE REASONS LIKE A MASTER TUTOR.',
        specs: [
            { label: 'CAPABILITY', value: 'DYNAMIC REASONING' },
            { label: 'SENSORY', value: 'NATIVE VISION' },
            { label: 'MEMORY', value: 'PERSISTENT CLOUD LOGS' },
            { label: 'PEDAGOGY', value: 'SOCRATIC LEARN MODE' },
        ],
    },
    {
        id: '01',
        icon: (<svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>),
        title: 'N-ZIP AI ENGINE',
        subtitle: 'LOW SPEC. HIGH PERFORMANCE.',
        status: 'ACTIVE',
        statusColor: 'text-green-400 border-green-400/30',
        description: 'AN AI ENGINE THAT RUNS ON POTATO-SPEC HARDWARE LIKE IT\'S A SUPERCOMPUTER. WE USE COMPRESSION-FIRST ARCHITECTURE INSPIRED BY ZIP FILE TECHNOLOGY TO SHRINK MODELS DOWN TO SIZES THAT WOULD MAKE YOUR PHONE LAUGH.',
        specs: [
            { label: 'ARCHITECTURE', value: 'ZIP-INSPIRED COMPRESSION' },
            { label: 'TARGET', value: '2GB RAM DEVICES' },
            { label: 'INFERENCE', value: 'SUB-SECOND' },
            { label: 'MODE', value: 'OFFLINE-FIRST' },
        ],
    },
    {
        id: '02',
        icon: (<svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/></svg>),
        title: 'MT CLOUD',
        subtitle: 'YOUR INFRASTRUCTURE, OUR OBSESSION.',
        status: 'BUILDING',
        statusColor: 'text-accent border-accent/30',
        description: 'A LIGHTWEIGHT CLOUD PLATFORM BUILT FOR DEVELOPERS WHO ARE TIRED OF PAYING AWS RENT. SELF-HOSTABLE, EDGE-OPTIMIZED, AND DESIGNED TO RUN ON ANYTHING FROM A RASPBERRY PI TO A BARE-METAL RACK.',
        specs: [
            { label: 'TYPE', value: 'SELF-HOSTED' },
            { label: 'DEPLOYMENT', value: 'EDGE-FIRST' },
            { label: 'SETUP', value: 'ONE-CLICK' },
            { label: 'AUDIENCE', value: 'INDIE DEVS' },
        ],
    },
    {
        id: '03',
        icon: (<svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="6" width="18" height="12" rx="2" ry="2"/><line x1="23" y1="13" x2="23" y2="11"/><line x1="6" y1="10" x2="6" y2="14"/><line x1="10" y1="10" x2="10" y2="14"/></svg>),
        title: 'ELECTRON AI',
        subtitle: 'TRAINING AI ON BATTERIES.',
        status: 'RESEARCH',
        statusColor: 'text-purple-400 border-purple-400/30',
        description: 'WHAT IF AI DIDN\'T NEED GPUS OR CPUS TO LEARN? WE\'RE EXPLORING HOW ELECTRON FLOW PATTERNS IN LITHIUM-ION BATTERIES CAN BE HARNESSED AS A COMPUTATIONAL SUBSTRATE FOR NEURAL NETWORK TRAINING.',
        specs: [
            { label: 'THEORY', value: 'ELECTROCHEMICAL COMPUTE' },
            { label: 'CONCEPT', value: 'BATTERY-AS-PROCESSOR' },
            { label: 'PIPELINE', value: 'ZERO-SILICON' },
            { label: 'GOAL', value: 'ENERGY-POSITIVE AI' },
        ],
    },
];

export default function Labs() {
    const [expandedId, setExpandedId] = useState(null);
    const navigate = useNavigate();

    const handleContact = () => {
        navigate('/');
        setTimeout(() => {
            document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
        }, 300);
    };

    return (
        <main className="w-full min-h-screen bg-background pt-32 pb-20 relative overflow-hidden z-0">
            
            {/* ═══ HERO SECTION ═══ */}
            <section className="w-full min-h-[90vh] flex flex-col relative overflow-hidden">
                
                {/* Header / Top Bar */}
                <div className="flex justify-between items-center px-6 md:px-12 py-6 border-b border-border bg-black/40 backdrop-blur-md relative z-10">
                    <motion.span 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                        className="font-mono text-[10px] text-muted uppercase tracking-widest"
                    >
                        {'/// EXPERIMENTAL DIVISION'}
                    </motion.span>
                    <motion.span 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                        className="font-mono text-[10px] text-muted uppercase tracking-widest"
                    >
                        {labProjects.length} ACTIVE PROTOCOLS
                    </motion.span>
                </div>

                {/* Massive Title Area */}
                <div className="flex-1 flex flex-col justify-center items-center px-6 relative z-10">
                    {/* Central Hero Dither Orb */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] h-[90vw] md:w-[40vw] md:h-[40vw] rounded-full overflow-hidden z-0 opacity-80 border border-white/10 shadow-[0_0_100px_rgba(56,189,248,0.2)] pointer-events-auto mix-blend-screen transition-all duration-700">
                        <Dither
                            waveColor={[56/255, 189/255, 248/255]} 
                            disableAnimation={false}
                            enableMouseInteraction
                            mouseRadius={0.3}
                            colorNum={4}
                            waveAmplitude={0.4}
                            waveFrequency={2}
                            waveSpeed={0.08}
                        />
                    </div>
                    
                    <div className="overflow-hidden relative z-10">
                        <motion.h1 
                            initial={{ y: "110%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                            className="text-[clamp(4rem,15vw,18rem)] font-heading uppercase font-extrabold text-foreground tracking-tighter leading-[0.85] text-center max-w-[95vw] drop-shadow-2xl z-10"
                        >
                            LABS
                        </motion.h1>
                    </div>
                    <div className="overflow-hidden mt-4 relative z-10">
                        <motion.p 
                            initial={{ y: "110%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 1.2, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                            className="font-mono text-xs md:text-sm text-accent font-bold uppercase tracking-[0.3em] text-center drop-shadow-md z-10"
                        >
                            WHERE WE BREAK THINGS ON PURPOSE
                        </motion.p>
                    </div>

                    {/* Stats Pill - Heavy Glassmorphism */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8, duration: 0.8 }}
                        className="flex gap-8 md:gap-16 mt-14 border border-white/10 rounded-full px-8 md:px-12 py-4 bg-white/5 backdrop-blur-2xl shadow-[0_0_40px_rgba(56,189,248,0.15)] relative z-10"
                    >
                        <div className="flex flex-col items-center">
                            <span className="font-mono text-xl font-bold text-accent">3</span>
                            <span className="font-mono text-[8px] text-white/70 uppercase tracking-widest">PROJECTS</span>
                        </div>
                        <div className="flex flex-col items-center border-l border-r border-white/10 px-8 md:px-16">
                            <span className="font-mono text-xl font-bold text-foreground">0</span>
                            <span className="font-mono text-[8px] text-white/70 uppercase tracking-widest">GPUS</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="font-mono text-xl font-bold text-foreground">&infin;</span>
                            <span className="font-mono text-[8px] text-white/70 uppercase tracking-widest">COFFEE</span>
                        </div>
                    </motion.div>
                </div>

                {/* Scroll Indicator */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5 }}
                    className="flex justify-center pb-8"
                >
                    <motion.div 
                        animate={{ y: [0, 6, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="flex flex-col items-center gap-2"
                    >
                        <span className="font-mono text-[9px] text-muted uppercase tracking-widest">SCROLL</span>
                        <svg className="w-3 h-3 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7" />
                        </svg>
                    </motion.div>
                </motion.div>
            </section>

            {/* ═══ INDEX HEADER ═══ */}
            <section className="px-6 md:px-12 py-20 border-y border-border bg-black/40 backdrop-blur-md min-h-[40vh] flex flex-col justify-center relative overflow-hidden group/title z-0">
                
                {/* Protocol Index Dither Strip (Desktop Only) */}
                <div className="absolute top-0 right-0 w-1/2 h-full hidden md:block opacity-0 group-hover/title:opacity-100 transition-opacity duration-1000 overflow-hidden z-0 border-l border-white/5 mask-radial-gradient pointer-events-auto">
                     <Dither
                        waveColor={[56/255, 189/255, 248/255]} 
                        disableAnimation={false}
                        enableMouseInteraction
                        mouseRadius={0.3}
                        colorNum={4}
                        waveAmplitude={0.2}
                        waveFrequency={4}
                        waveSpeed={0.03}
                    />
                </div>
                
                <div className="flex justify-between items-start mb-8 w-full relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                        <span className="font-mono text-[10px] text-muted uppercase tracking-widest">{'/// PROTOCOL INDEX'}</span>
                    </div>
                    <span className="font-mono text-[10px] text-muted uppercase tracking-widest text-right max-w-[200px]">
                        R&D PROJECTS IN DEVELOPMENT
                    </span>
                </div>
                <div className="overflow-hidden relative z-10">
                    <motion.h2 
                        initial={{ y: "100%" }}
                        whileInView={{ y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                        className="text-[clamp(4rem,10vw,12rem)] font-heading uppercase font-extrabold text-foreground tracking-tighter leading-[0.85] relative z-10"
                    >
                        THE INDEX.
                    </motion.h2>
                </div>
            </section>

            {/* ═══ PROJECT ROWS ═══ */}
            {labProjects.map((project, index) => (
                <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.6, delay: index * 0.05 }}
                    className="border-b border-border bg-black/20 backdrop-blur-sm"
                >
                    {/* Main Row */}
                    <button 
                        onClick={() => setExpandedId(expandedId === project.id ? null : project.id)}
                        className="w-full relative flex flex-col md:flex-row md:items-center justify-between px-6 md:px-12 py-12 md:py-16 cursor-pointer group text-left transition-all duration-500 overflow-hidden hover:bg-white/[0.02]"
                    >
                        {/* Subtle glow on hover */}
                        <div className="absolute inset-0 bg-gradient-to-r from-accent/0 via-accent/5 to-accent/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-0 pointer-events-none translate-x-[-100%] group-hover:translate-x-[100%] ease-in-out" />
                        
                        <div className="flex items-center gap-6 md:gap-10 relative z-10">
                            {/* Icon - Frosted */}
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-border/50 flex items-center justify-center text-muted group-hover:text-accent group-hover:bg-accent/10 group-hover:border-accent/50 group-hover:shadow-[0_0_30px_rgba(56,189,248,0.2)] transition-all duration-500 flex-shrink-0 backdrop-blur-md">
                                {project.icon}
                            </div>
                            
                            {/* Title & Subtitle */}
                            <div>
                                <div className="flex items-center gap-4 flex-wrap mb-2">
                                    <h3 className="text-4xl md:text-6xl lg:text-7xl font-heading font-extrabold uppercase tracking-tighter leading-[0.85] text-foreground group-hover:text-accent transition-colors duration-500">
                                        {project.title}
                                    </h3>
                                    <span className={`font-mono text-[10px] font-bold uppercase tracking-widest px-3 py-1 border rounded-full ${project.statusColor} backdrop-blur-sm self-start mt-2 md:mt-0`}>
                                        {project.status}
                                    </span>
                                </div>
                                <span className="font-mono text-xs md:text-sm text-muted font-bold uppercase tracking-[0.2em] mt-2 block group-hover:text-foreground/80 transition-colors">
                                    {project.subtitle}
                                </span>
                            </div>
                        </div>
                        
                        {/* Expand Button */}
                        <div className="flex items-center gap-3 mt-4 md:mt-0">
                            <span className="font-mono text-[10px] text-muted uppercase tracking-widest hidden md:block">
                                {expandedId === project.id ? 'CLOSE' : 'DETAILS'}
                            </span>
                            <motion.div 
                                animate={{ rotate: expandedId === project.id ? 45 : 0 }}
                                transition={{ duration: 0.25 }}
                                className="w-10 h-10 rounded-full border border-border flex items-center justify-center group-hover:border-accent group-hover:text-accent transition-colors flex-shrink-0"
                            >
                                <span className="font-heading text-lg leading-none">+</span>
                            </motion.div>
                        </div>
                    </button>

                    {/* Expanded Content */}
                    <AnimatePresence>
                        {expandedId === project.id && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                className="overflow-hidden"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-border/50">
                                    {/* Description */}
                                    <div className="px-6 md:px-12 py-8 md:border-r border-border/50">
                                        <span className="font-mono text-[9px] text-accent uppercase tracking-widest block mb-4">OVERVIEW</span>
                                        <p className="font-mono text-xs text-foreground/70 uppercase leading-[2]">
                                            {project.description}
                                        </p>
                                    </div>
                                    {/* Specs */}
                                    <div className="px-6 md:px-12 py-8 flex flex-col justify-between h-full min-h-[200px]">
                                        <div>
                                            <span className="font-mono text-[9px] text-accent uppercase tracking-widest block mb-4">SPECIFICATION</span>
                                            <div className="grid grid-cols-2 gap-4">
                                                {project.specs.map((spec) => (
                                                    <div key={spec.label} className="flex flex-col gap-0.5">
                                                        <span className="font-mono text-[9px] text-muted uppercase tracking-widest">{spec.label}</span>
                                                        <span className="font-mono text-xs text-foreground uppercase font-bold">{spec.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                        {project.id === '00' && (
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate('/isho');
                                                }}
                                                className="mt-8 px-8 py-4 bg-pink-400/10 text-pink-400 border border-pink-400/30 font-mono text-[10px] md:text-xs uppercase font-bold tracking-[0.2em] rounded-full hover:bg-pink-400 hover:text-black hover:shadow-[0_0_20px_rgba(244,114,182,0.4)] transition-all flex items-center justify-center gap-3 w-full md:w-auto self-start group"
                                            >
                                                EXPERIENCE ISHO
                                                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                                            </button>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                </motion.div>
            ))}

            {/* ═══ CTA SECTION ═══ */}
            <div className="px-6 md:px-12 py-32 md:py-48 bg-black/40 backdrop-blur-md relative overflow-hidden group flex flex-col items-center justify-center text-center border-t border-border">
                {/* Pulse background on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-1000 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-accent via-transparent to-transparent mix-blend-screen pointer-events-none" />
                
                <div className="relative z-10 flex flex-col items-center max-w-4xl mx-auto">
                    <motion.h3 
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="text-[clamp(3rem,8vw,10rem)] font-heading uppercase font-extrabold tracking-tighter leading-[0.85] text-foreground mb-6"
                    >
                        WANT IN<span className="text-accent">?</span>
                    </motion.h3>
                    <p className="font-mono text-sm md:text-base text-muted uppercase tracking-[0.3em] font-bold mb-12">
                        WE'RE LOOKING FOR WILD MINDS AND BRAVE PARTNERS.
                    </p>
                    <button 
                        onClick={handleContact}
                        className="group/btn relative overflow-hidden rounded-full font-mono text-xs font-bold uppercase tracking-[0.2em] px-12 py-5 bg-transparent text-accent border border-accent/50 hover:border-accent transition-colors duration-500"
                    >
                        <div className="absolute inset-0 bg-accent translate-y-[100%] group-hover/btn:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
                        <span className="relative z-10 group-hover/btn:text-background transition-colors duration-500">LET'S TALK</span>
                    </button>
                </div>
            </div>

        </main>
    );
}
